/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React from 'react';
import { Link } from 'react-router-dom';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Tooltip from '@mui/material/Tooltip';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Trash2 } from 'lucide-react';

import { getProductStorePath } from 'renderer/features/products-page/utils/productPath';
import { CartLinePriceDisplay } from './CartLinePriceDisplay';
import { cartItemKey } from './cartItemKey';
import { isUpgradeItem } from './cartLineKinds';
import { isOnSale } from './cartPricing';
import { getLineDisplayTotal } from './cartTotals';
import type { SerialRow, SerialRowMap } from './cartTypes';
import { UpgradeSerialEditor } from './UpgradeSerialEditor';

type UpdateCartPayload = {
  cartItemId: string;
  quantity?: number;
  platform?: string;
  hostApplication?: any;
};

export interface CartLineItemProps {
  item: any;
  baseUrl: string;
  serialRowsByItem: SerialRowMap;
  setSerialRowsByItem: React.Dispatch<React.SetStateAction<SerialRowMap>>;
  validateSerialForItem: (
    item: any,
    rowIndex: number,
    rawSerial: string,
    targetHostAppIdOverride?: string,
  ) => void | Promise<void>;
  removeSerialRow: (item: any, rowIndex: number) => void;
  commitUpgradeSerials: (item: any, rows: SerialRow[]) => void;
  getSupportedPlatformsForItem: (item: any) => string[];
  onUpdateCartItem: (arg: UpdateCartPayload) => Promise<unknown>;
  onQuantityChange: (item: any, quantity: number) => void;
  onRemoveLine: (cartItemId: string) => void;
  controlsDisabled?: boolean;
  /** Choose-min line charge; when omitted falls back to sale-aware × qty. */
  lineDisplayTotal?: number;
}

export function CartLineItem({
  item,
  baseUrl,
  serialRowsByItem,
  setSerialRowsByItem,
  validateSerialForItem,
  removeSerialRow,
  commitUpgradeSerials,
  getSupportedPlatformsForItem,
  onUpdateCartItem,
  onQuantityChange,
  onRemoveLine,
  controlsDisabled = false,
  lineDisplayTotal,
}: CartLineItemProps) {
  const lineKey = cartItemKey(item);
  const cartItemId = lineKey;

  const image = item.productId.productImageId
    ? `${baseUrl}/product-images/${item.productId.productImageId}`
    : '/images/placeholder.png';

  const isItemAllAccess = String(item.productId.name || '')
    .toLowerCase()
    .includes('all access');
  const allHostApp = item.productId.supportedHostApplications?.find(
    (s: any) => s.displayName === 'All Host Apps',
  );
  const serialRows = serialRowsByItem[lineKey] ?? [];
  const serialRowsContainAllHostApps = serialRows.some(
    (r) => (r.targetHostApp || r.hostApp)?.displayName === 'All Host Apps',
  );
  const productStorePath = getProductStorePath(item.productId);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '140px 1fr auto' },
        gap: { xs: 1.5, sm: 2 },
        pb: 2,
        mb: 2,
        borderBottom: '1px solid #ccc',
        alignItems: { sm: 'flex-start' },
      }}
    >
      <Link to={productStorePath} style={{ textDecoration: 'none' }}>
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', sm: 140 },
            height: { xs: 200, sm: 160 },
            border: '1px solid #ccc',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {isOnSale(item.productId, item.discountId, item.hostApplication) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                bgcolor: 'error.main',
                color: 'common.white',
                fontWeight: 'bold',
                fontSize: 14,
                textAlign: 'center',
                py: 0.5,
                zIndex: 2,
              }}
            >
              SALE
            </Box>
          )}
          <Box
            component="img"
            src={image}
            alt={item.productId.name}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Link>

      <Box sx={{ minWidth: 0 }}>
        <Link to={productStorePath} style={{ textDecoration: 'none' }}>
          <Typography sx={{ fontWeight: 700, color: '#222', mt: 0.5 }}>
            {item.productId.name}
          </Typography>
          <Typography
            sx={{
              fontStyle: 'italic',
              color: '#888',
              mt: -0.5,
              mb: 0.5,
            }}
          >
            {item.productId.category}
          </Typography>
        </Link>

        <CartLinePriceDisplay
          item={item}
          allHostApp={allHostApp}
          serialRowsContainAllHostApps={serialRowsContainAllHostApps}
        />

        {isUpgradeItem(item) &&
          serialRowsContainAllHostApps &&
          allHostApp?.priceOverride && (
            <Chip
              size="small"
              sx={{ my: 1 }}
              label={`+${allHostApp.priceOverride.toFixed(2)} - ${
                allHostApp.displayName
              }`}
            />
          )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 1,
          }}
        >
          <Box
            component="label"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box
              sx={{
                fontWeight: 600,
                minWidth: 100,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Platform:
            </Box>
            <Select
              size="small"
              value={item.platform}
              disabled={controlsDisabled}
              sx={{ minWidth: 180, bgcolor: 'background.paper' }}
              onChange={async (e) => {
                const nextPlatform = e.target.value;

                if (isUpgradeItem(item)) {
                  await onUpdateCartItem({
                    cartItemId,
                    platform: nextPlatform,
                  });
                  return;
                }

                await onUpdateCartItem({
                  cartItemId,
                  quantity: item.quantity,
                  platform: nextPlatform,
                  hostApplication: item.hostApplication,
                });
              }}
            >
              {getSupportedPlatformsForItem(item).map((platform: string) => (
                <MenuItem key={platform} value={platform}>
                  {platform}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {!isUpgradeItem(item) && (
            <>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box sx={{ fontWeight: 600, minWidth: 100 }}>Host App:</Box>
                <Select
                  size="small"
                  displayEmpty
                  value={item.hostApplication?.id ?? ''}
                  disabled={controlsDisabled}
                  sx={{ minWidth: 180, bgcolor: 'background.paper' }}
                  onChange={async (e) => {
                    const selectedApp =
                      item.productId.supportedHostApplications?.find(
                        (app: any) => app.id === e.target.value,
                      ) || null;

                    await onUpdateCartItem({
                      cartItemId,
                      quantity: item.quantity,
                      platform: item.platform,
                      hostApplication: selectedApp
                        ? {
                            id: selectedApp.id,
                            displayName: selectedApp.displayName,
                            version: selectedApp.version,
                            priceOverride: selectedApp.priceOverride,
                          }
                        : null,
                    });
                  }}
                >
                  <MenuItem value="">Select Host App...</MenuItem>
                  {(item.productId.supportedHostApplications || []).map(
                    (app: any) => (
                      <MenuItem key={app.id} value={app.id}>
                        {app.displayName}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </Box>

              {!item.hostApplication?.id && !isItemAllAccess && (
                <Typography sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>
                  Please select a Host App before proceeding.
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          mt: { xs: 1, sm: 0 },
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 14,
            display: { xs: 'none', sm: 'inline' },
          }}
        >
          Quantity:
        </Typography>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'stretch',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            height: 44,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <Box
            component="input"
            type="tel"
            min={1}
            disabled={controlsDisabled}
            value={
              isUpgradeItem(item)
                ? serialRowsByItem[lineKey]?.length || 1
                : item.quantity
            }
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onQuantityChange(
                item,
                Math.max(1, parseInt(e.target.value, 10) || 1),
              )
            }
            sx={{
              width: 64,
              px: 1,
              border: 0,
              outline: 'none',
              textAlign: 'center',
              fontSize: 16,
              lineHeight: '44px',
              '&::-webkit-outer-spin-button,&::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              MozAppearance: 'textfield',
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <IconButton
              size="small"
              aria-label="Increase quantity"
              disabled={controlsDisabled}
              onClick={() =>
                onQuantityChange(
                  item,
                  (isUpgradeItem(item)
                    ? serialRowsByItem[lineKey]?.length || 1
                    : item.quantity) + 1,
                )
              }
              sx={{ borderRadius: 0, width: 32, height: 22, p: 0 }}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>

            <Divider flexItem sx={{ m: 0, borderColor: 'divider' }} />

            <IconButton
              size="small"
              aria-label="Decrease quantity"
              disabled={controlsDisabled}
              onClick={() =>
                onQuantityChange(
                  item,
                  Math.max(
                    1,
                    (isUpgradeItem(item)
                      ? serialRowsByItem[lineKey]?.length || 1
                      : item.quantity) - 1,
                  ),
                )
              }
              sx={{ borderRadius: 0, width: 32, height: 22, p: 0 }}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Typography sx={{ fontWeight: 700, color: '#222', mt: 0.5 }}>
          $
          {(
            lineDisplayTotal ?? getLineDisplayTotal(item, serialRowsByItem)
          ).toFixed(2)}
        </Typography>

        <Tooltip title="Remove item" arrow>
          <IconButton
            onClick={() => onRemoveLine(cartItemId)}
            size="small"
            aria-label="Remove item"
            disabled={controlsDisabled}
            sx={{ minHeight: 44 }}
          >
            <Trash2 size={20} color="black" />
          </IconButton>
        </Tooltip>
      </Box>

      {isUpgradeItem(item) && (
        <Box
          sx={{
            gridColumn: '1 / 4',
            pl: { xs: 0, sm: '140px' },
          }}
        >
          <UpgradeSerialEditor
            item={item}
            rows={serialRows}
            setSerialRowsByItem={setSerialRowsByItem}
            validateSerialForItem={validateSerialForItem}
            removeSerialRow={removeSerialRow}
            commitUpgradeSerials={commitUpgradeSerials}
          />
        </Box>
      )}
    </Box>
  );
}
